/**
 * Org Structure Tree
 *
 * The DEFAULT organisation view — a fast, indented Role-Master taxonomy tree
 * (Function -> Department -> Team 1/2/3 -> Role) rendered from the cached
 * `GET /org/structure/tree` skeleton. Replaces the spatial OrgChartVisx as the
 * primary view (that hand-rolled node-graph + framer-motion-per-node caused the
 * 45s admin freeze).
 *
 * Performance: collapsed subtrees are never mounted; people under a role are
 * loaded lazily (and cached per role) only when the role is expanded. Empty
 * containers (rollup.roles === 0) are dropped entirely.
 *
 * Search jumps to the match (expands the whole ancestor path + scrolls + rings)
 * instead of leaving an "ancestor wall" — the expanded path IS the breadcrumb.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Building2, Folder, Briefcase, ChevronRight, ChevronDown, Search, X,
  GraduationCap, Loader2, AlertCircle, ChevronsDownUp, ChevronsUpDown,
  Users, Filter, RefreshCw, MapPin, Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import organizationService, {
  OrgStructureTree as OrgStructureTreeData,
  OrgStructureTreeContainer, OrgStructureTreeRole, OrgStructureTreeNode,
  OrgStructureRollup, Position, PositionStatus,
} from '@/services/api/organizationService';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import { orgRowPadding, orgRowTint } from './orgTreeDepth';

// =============================================================================
// CONSTANTS / HELPERS
// =============================================================================

const FILLED_COLOR = '#1D9E75'; // teal — filled seats
// Cap the rows rendered under a single role so a 1400-person role neither mounts
// 1400 DOM rows nor fires 1400 photo requests. "Show all N" reveals the rest.
const PEOPLE_ROW_CAP = 60;

const POSITION_STATUS_LABEL: Record<PositionStatus, string> = {
  ACTIVE: 'Active',
  VACANT: 'Vacant',
  FROZEN: 'Frozen',
  PENDING_APPROVAL: 'Pending',
  RETIRED: 'Retired',
};

const isRole = (n: OrgStructureTreeNode): n is OrgStructureTreeRole => n.type === 'role';

// Sentinel entry_id for the server's synthetic "Unassigned (no role)" branch — the
// bucket of non-RETIRED positions that resolve to no active Role Master role. The
// client recognises it to gather orphans locally + skip the (non-existent) role drawer.
// Must match the server's role:__unassigned__ leaf (organization.py get_org_structure_tree).
const ORPHAN_ENTRY_ID = -1;
const ORPHAN_FUNCTION_ID = 'fn:__unassigned__'; // server's synthetic "Unassigned (no role)" top-level node
const isOrphanRole = (n: OrgStructureTreeNode): boolean => isRole(n) && n.entry_id === ORPHAN_ENTRY_ID;

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

interface Filters {
  grades: Set<string>;
  academicOnly: boolean;
  vacantOnly: boolean;
}

function roleMatches(r: OrgStructureTreeRole, f: Filters): boolean {
  if (f.grades.size > 0 && !(r.grade_level && f.grades.has(r.grade_level))) return false;
  if (f.academicOnly && !r.is_academic) return false;
  if (f.vacantOnly && r.rollup.vacant <= 0) return false;
  return true;
}

function sumRollups(nodes: OrgStructureTreeNode[]): OrgStructureRollup {
  return nodes.reduce<OrgStructureRollup>(
    (acc, n) => ({
      roles: acc.roles + n.rollup.roles,
      seats: acc.seats + n.rollup.seats,
      filled: acc.filled + n.rollup.filled,
      vacant: acc.vacant + n.rollup.vacant,
    }),
    { roles: 0, seats: 0, filled: 0, vacant: 0 },
  );
}

/**
 * Prune a container to only roles matching the filters (and the containers that
 * still hold a surviving role). Rollups are recomputed from survivors so the
 * mini-bars reflect the filtered view. Containers that end up empty — including
 * originally-empty ones (rollup.roles === 0) — are dropped (returns null).
 */
function pruneContainer(c: OrgStructureTreeContainer, f: Filters): OrgStructureTreeContainer | null {
  const children: OrgStructureTreeNode[] = [];
  for (const child of c.children) {
    if (isRole(child)) {
      if (roleMatches(child, f)) children.push(child);
    } else {
      const pruned = pruneContainer(child, f);
      if (pruned) children.push(pruned);
    }
  }
  if (children.length === 0) return null;
  return { ...c, children, rollup: sumRollups(children) };
}

// Lazy people state, cached per RoleMasterEntry id.
interface PeopleState {
  loading: boolean;
  error: boolean;
  positions: Position[];
}

// Flat search index entry (built once from the raw skeleton).
interface IndexEntry {
  id: string;
  type: OrgStructureTreeNode['type'];
  name: string;         // container name or role title
  roleCode?: string;
  entryId?: number;
  path: string[];       // ancestor container ids from function down to and incl. self
  functionName: string;
}

// =============================================================================
// ROLLUP MINI-BAR
// =============================================================================

const RollupBar: React.FC<{ rollup: OrgStructureRollup }> = ({ rollup }) => {
  const total = rollup.filled + rollup.vacant;
  const filledPct = total > 0 ? (rollup.filled / total) * 100 : 0;
  const vacantPct = total > 0 ? (rollup.vacant / total) * 100 : 0;
  return (
    <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted" title={`${Math.round(rollup.filled)} filled / ${Math.round(rollup.vacant)} vacant`}>
      <div className="flex h-full w-full">
        <div style={{ width: `${filledPct}%`, backgroundColor: FILLED_COLOR }} />
        <div style={{ width: `${vacantPct}%` }} className="bg-amber-400" />
      </div>
    </div>
  );
};

// =============================================================================
// PERSON ROWS (lazy, under a role)
// =============================================================================

const PersonRow: React.FC<{
  position: Position;
  depth: number;
  highlight: boolean;
  // Show the position TITLE on the row. Used for the "Unlinked positions" bucket
  // where each seat has a distinct title and there's no role heading to carry it
  // (a normal role's rows all share the role title, so it's omitted there).
  showTitle?: boolean;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
}> = ({ position, depth, highlight, showTitle, onOpenPerson }) => {
  const vacant = position.is_vacant || !position.incumbent_user_id;
  const photo = useUserPhoto(vacant ? null : position.incumbent_user_id);
  const campus = position.campus_name || position.campus_code || '';
  const pad = { paddingLeft: orgRowPadding(depth) };

  if (vacant) {
    // Clickable so a vacant seat opens its drawer (Set reporting line / Assign) —
    // the drawer keys off position.id, no incumbent required.
    return (
      <button
        type="button"
        onClick={() => onOpenPerson?.(null, position.id)}
        style={pad}
        className={cn('flex w-full items-center py-1.5 pr-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50', orgRowTint(depth))}
      >
        <span className="flex min-w-0 items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
          <span className="shrink-0 font-medium">Vacant</span>
          {showTitle && position.title && (
            <span className="truncate font-medium text-foreground/80">{position.title}</span>
          )}
          <span className="shrink-0 font-mono text-amber-600/80 dark:text-amber-300/70">{position.position_code}</span>
          {campus && <span className="shrink-0">· {campus}</span>}
        </span>
      </button>
    );
  }

  const rowStyle: React.CSSProperties = highlight
    ? { ...pad, boxShadow: `inset 0 0 0 2px ${FILLED_COLOR}`, backgroundColor: 'rgba(29,158,117,0.08)' }
    : pad;

  return (
    <button
      type="button"
      onClick={() => onOpenPerson?.(position.incumbent_user_id ?? null, position.id)}
      style={rowStyle}
      className={cn('flex w-full cursor-pointer items-center gap-2.5 rounded-md py-1.5 pr-3 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50', !highlight && orgRowTint(depth))}
    >
      <Avatar className="h-6 w-6 shrink-0">
        {photo && <AvatarImage src={photo} alt={position.incumbent_name || ''} />}
        <AvatarFallback className="bg-heritage-blue/10 text-[10px] text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
          {getInitials(position.incumbent_name)}
        </AvatarFallback>
      </Avatar>
      <span className="truncate font-medium text-foreground">{position.incumbent_name}</span>
      {showTitle && position.title && (
        <span className="truncate text-xs text-muted-foreground">· {position.title}</span>
      )}
      {campus && <span className="truncate text-xs text-muted-foreground">{campus}</span>}
      <Badge variant="secondary" className="ml-auto shrink-0 text-[10px]">
        {POSITION_STATUS_LABEL[position.status] || position.status}
      </Badge>
    </button>
  );
};

const RolePeople: React.FC<{
  entryId: number;
  depth: number;
  state?: PeopleState;
  onLoad: (entryId: number) => void;
  highlightPositionId: number | null;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  // Person-level predicate (campus + financial dimensions). Filters only the
  // loaded people rows — the skeleton carries no campus/dim data, so un-expanded
  // roles are never pruned by these.
  personFilter: (p: Position) => boolean;
}> = ({ entryId, depth, state, onLoad, highlightPositionId, onOpenPerson, personFilter }) => {
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!state) onLoad(entryId);
  }, [entryId, state, onLoad]);

  const pad = { paddingLeft: orgRowPadding(depth) };

  if (!state || state.loading) {
    return (
      <div style={pad} className="flex items-center gap-2 py-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading people…
      </div>
    );
  }
  if (state.error) {
    return (
      <div style={pad} className="py-1.5 text-xs text-destructive">
        Couldn’t load people.{' '}
        <button type="button" className="underline" onClick={() => onLoad(entryId)}>Retry</button>
      </div>
    );
  }
  if (state.positions.length === 0) {
    return (
      <div style={pad} className="py-1.5 text-xs text-muted-foreground">No positions mapped to this role.</div>
    );
  }
  // Campus / financial-dimension filters narrow the loaded people client-side.
  const visible = state.positions.filter(personFilter);
  if (visible.length === 0) {
    return (
      <div style={pad} className="py-1.5 text-xs text-muted-foreground">
        No people under this role match the current campus / dimension filters.
      </div>
    );
  }
  // Don't cap past a search-jump target: if the highlighted person is in the
  // tail, reveal everything so the ring (and scroll) still lands on them.
  const highlightBeyondCap =
    highlightPositionId != null &&
    visible.findIndex((p) => p.id === highlightPositionId) >= PEOPLE_ROW_CAP;
  const capped = !showAll && !highlightBeyondCap && visible.length > PEOPLE_ROW_CAP;
  const shown = capped ? visible.slice(0, PEOPLE_ROW_CAP) : visible;

  return (
    <>
      {shown.map((p) => (
        <PersonRow
          key={p.id}
          position={p}
          depth={depth}
          highlight={highlightPositionId === p.id}
          showTitle={entryId === ORPHAN_ENTRY_ID}
          onOpenPerson={onOpenPerson}
        />
      ))}
      {capped && (
        <div style={pad} className="py-1.5 pr-3">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-heritage-blue hover:bg-muted dark:text-heritage-blue-100"
          >
            <ChevronDown className="h-3.5 w-3.5" />
            Show all {visible.length.toLocaleString()}
          </button>
        </div>
      )}
    </>
  );
};

// =============================================================================
// TREE BRANCH (recursive; collapsed subtrees never mount)
// =============================================================================

interface BranchProps {
  node: OrgStructureTreeNode;
  depth: number;
  expanded: Set<string>;
  // Toggling a ROLE always flips the real `expanded` set (people stay lazy);
  // toggling a non-role container while filtering flips `collapsedInFilter`.
  onToggle: (id: string, isRoleNode: boolean) => void;
  registerRef: (id: string, el: HTMLElement | null) => void;
  highlightId: string | null;
  peopleByEntry: Map<number, PeopleState>;
  onLoadPeople: (entryId: number) => void;
  highlightPositionId: number | null;
  onOpenRole?: (entryId: number, roleCode: string) => void;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  personFilter: (p: Position) => boolean;
  // When the campus/dimension filters are active, the set of node ids to render
  // (matching roles + their ancestors). null = no person-level filter, render the
  // full (grade/academic/vacant-pruned) skeleton.
  visibleIds: Set<string> | null;
  // Filtered header rollups per node id while the person-level filter is active;
  // null = use the server totals on `node.rollup`.
  filteredRollupById: Map<string, OrgStructureRollup> | null;
  // While filtering, non-role containers render expanded-by-default (so matching
  // roles surface) EXCEPT ids in `collapsedInFilter` (the user's explicit collapses
  // this filter session). Roles are unaffected — they keep their `expanded` state.
  forceExpand: boolean;
  collapsedInFilter: Set<string>;
}

const TreeBranch: React.FC<BranchProps> = (props) => {
  const { node, depth, expanded, onToggle, registerRef, highlightId,
          visibleIds, filteredRollupById, forceExpand, collapsedInFilter } = props;
  const role = isRole(node);
  // Roles keep their normal (lazy) expand state even while filtering — opening one
  // is what fetches its people. Non-role containers are expanded-by-default while
  // filtering, minus the user's explicit collapses this session.
  const isExpanded = (forceExpand && !role)
    ? !collapsedInFilter.has(node.id)
    : expanded.has(node.id);
  const highlighted = highlightId === node.id;
  const Icon = node.type === 'function' ? Building2 : node.type === 'role' ? Briefcase : Folder;
  // While the person-level filter prunes the tree the header must show the FILTERED
  // counts (else a Function still reads its full server total); fall back to the
  // server rollup when no such filter is active.
  const rollup = filteredRollupById?.get(node.id) ?? node.rollup;
  const roles = Math.round(rollup.roles);
  const seats = Math.round(rollup.seats);
  const filled = Math.round(rollup.filled);
  const vacant = Math.round(rollup.vacant);

  return (
    <div>
      <div
        ref={(el) => registerRef(node.id, el)}
        style={{
          paddingLeft: orgRowPadding(depth),
          ...(highlighted
            ? { boxShadow: `inset 0 0 0 1px ${FILLED_COLOR}`, backgroundColor: 'rgba(29,158,117,0.10)' }
            : {}),
        }}
        className={cn(
          'flex items-center gap-2 border-b border-border/40 py-2 pr-3 transition-colors',
          !highlighted && orgRowTint(depth),
          !highlighted && 'hover:bg-muted/60',
        )}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggle(node.id, role); }}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <Icon
          className={cn(
            'h-4 w-4 shrink-0',
            node.type === 'function' && 'text-heritage-blue dark:text-heritage-blue-200',
            node.type === 'role' && 'text-muted-foreground',
            (node.type === 'department' || node.type === 'team') && 'text-future-blue-600 dark:text-future-blue-300',
          )}
        />

        {isRole(node) ? (
          isOrphanRole(node) ? (
            // Synthetic "Unlinked positions" bucket — not a real Role Master role, so
            // no role drawer; clicking toggles the people list. Styled amber to read
            // as a data-quality bucket (positions that should be linked or retired).
            <button
              type="button"
              onClick={() => onToggle(node.id, role)}
              className="flex min-w-0 items-center gap-2 text-left"
            >
              <span className="truncate text-sm italic text-amber-600 dark:text-amber-400">{node.title}</span>
            </button>
          ) : (
          <button
            type="button"
            onClick={() =>
              props.onOpenRole
                ? props.onOpenRole(node.entry_id, node.role_code)
                : onToggle(node.id, role)
            }
            className="flex min-w-0 items-center gap-2 text-left"
          >
            <span className="shrink-0 rounded bg-heritage-blue/10 px-1.5 py-0.5 font-mono text-xs text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
              {node.role_code}
            </span>
            <span className="truncate text-sm text-foreground">{node.title}</span>
            {node.grade_level && (
              <Badge variant="outline" className="shrink-0 text-[10px]">{node.grade_level}</Badge>
            )}
            {node.is_academic && (
              <span className="flex shrink-0 items-center gap-1 text-[10px] text-future-blue-600 dark:text-future-blue-300">
                <GraduationCap className="h-3 w-3" /> Academic
              </span>
            )}
          </button>
          )
        ) : (
          <button
            type="button"
            onClick={() => onToggle(node.id, role)}
            className="flex min-w-0 items-center text-left"
          >
            <span
              className={cn(
                'truncate text-sm',
                node.type === 'function' ? 'font-semibold text-foreground' : 'font-medium text-foreground/90',
              )}
            >
              {node.name}
            </span>
          </button>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-3">
          <div className="hidden flex-col items-end leading-tight sm:flex">
            <span className="text-[11px] text-muted-foreground">
              {role
                ? `${seats} seat${seats !== 1 ? 's' : ''}`
                : `${roles} role${roles !== 1 ? 's' : ''} · ${seats} seat${seats !== 1 ? 's' : ''}`}
            </span>
            <span className="text-[11px]">
              <span style={{ color: FILLED_COLOR }}>{filled} filled</span>
              <span className="text-muted-foreground"> / </span>
              <span className="text-amber-600 dark:text-amber-400">{vacant} vacant</span>
            </span>
          </div>
          <RollupBar rollup={rollup} />
        </div>
      </div>

      {isExpanded && !isRole(node) &&
        (visibleIds ? node.children.filter((c) => visibleIds.has(c.id)) : node.children).map((child) => (
          <TreeBranch key={child.id} {...props} node={child} depth={depth + 1} />
        ))}

      {isExpanded && isRole(node) && (
        <RolePeople
          entryId={node.entry_id}
          depth={depth + 1}
          state={props.peopleByEntry.get(node.entry_id)}
          onLoad={props.onLoadPeople}
          highlightPositionId={props.highlightPositionId}
          onOpenPerson={props.onOpenPerson}
          personFilter={props.personFilter}
        />
      )}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface OrgStructureTreeProps {
  onOpenRole?: (entryId: number, roleCode: string) => void;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  // Self-populated facet option lists (campus code + F&O financial dimensions),
  // lifted from OrganizationPage's positions so every tree shares one source of
  // truth. Active-filter state stays internal to this component.
  campusOptions?: string[];
  dimCampusOptions?: string[];
  dimDeptOptions?: string[];
  dimFacultyOptions?: string[];
  workerTypeOptions?: string[];   // incumbent employment_category (filled seats)
  positionTypeOptions?: string[]; // seat's F&O type normalized (incl. vacant)
  // All loaded positions — the source for the person-level PRUNE (a role survives
  // iff >=1 of its positions passes the filter) AND the filtered header rollups.
  // RETIRED are dropped internally.
  positions?: Position[];
  // Bumped by the parent after a mutation to trigger an in-place refetch.
  reloadKey?: number;
}

export const OrgStructureTree: React.FC<OrgStructureTreeProps> = ({
  onOpenRole,
  onOpenPerson,
  campusOptions = [],
  dimCampusOptions = [],
  dimDeptOptions = [],
  dimFacultyOptions = [],
  workerTypeOptions = [],
  positionTypeOptions = [],
  positions = [],
  reloadKey = 0,
}) => {
  const [data, setData] = useState<OrgStructureTreeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // While the campus/dimension filter prunes the tree, non-role containers render
  // expanded-by-default; this holds the ones the user EXPLICITLY collapsed during
  // the current filter session. Kept separate from `expanded` so it never leaks
  // into the unfiltered view — clearing the filter reverts to `expanded` untouched.
  const [collapsedInFilter, setCollapsedInFilter] = useState<Set<string>>(new Set());
  const [peopleByEntry, setPeopleByEntry] = useState<Map<number, PeopleState>>(new Map());

  // Search
  const [term, setTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [peopleResults, setPeopleResults] = useState<Position[]>([]);
  const [peopleSearching, setPeopleSearching] = useState(false);

  // Filters (skeleton/role-level)
  const [functionFilter, setFunctionFilter] = useState<string>('all');
  const [gradeFilter, setGradeFilter] = useState<Set<string>>(new Set());
  const [academicOnly, setAcademicOnly] = useState(false);
  const [vacantOnly, setVacantOnly] = useState(false);

  // Person-level filters (campus + F&O financial dimensions). These narrow the
  // lazily-loaded people rows under each role; they never prune the skeleton
  // (which carries no campus/dim data on un-expanded roles).
  const [campusFilter, setCampusFilter] = useState<string>('all');
  const [dimCampusFilter, setDimCampusFilter] = useState<string>('all');
  const [dimDeptFilter, setDimDeptFilter] = useState<string>('all');
  const [dimFacultyFilter, setDimFacultyFilter] = useState<string>('all');
  // Two independent employment axes. Worker type = incumbent's employment_category
  // (a vacant seat has no worker → it can't match a worker-type filter). Position
  // type = the seat's own F&O type normalized (present incl. when vacant).
  const [workerTypeFilter, setWorkerTypeFilter] = useState<Set<string>>(new Set());
  const [positionTypeFilter, setPositionTypeFilter] = useState<Set<string>>(new Set());

  // Jump / highlight
  const [focusedPath, setFocusedPath] = useState<string[] | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [highlightPositionId, setHighlightPositionId] = useState<number | null>(null);

  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  // Close the search dropdown on an outside click, without an overlay that
  // would swallow the first click on the toolbar filters.
  useEffect(() => {
    if (!showResults) return;
    const onDown = (e: PointerEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [showResults]);

  // --- Load skeleton ---------------------------------------------------------
  // `silent` refetches in place (no loading skeleton, current tree stays on
  // screen) — used for the post-mutation refresh so it doesn't flash/reset.
  const load = useCallback(async (refresh = false, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const tree = await organizationService.getStructureTree(refresh);
      setData(tree);
    } catch (err) {
      console.error('Failed to load org structure tree:', err);
      if (!silent) setError('Failed to load the organisation structure. Please try again.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Parent bumps `reloadKey` after a mutation (reporting-line / assign) so this
  // self-loading tree picks up the fresh projection. Skip the initial 0 (the
  // mount effect above already fetched); refetch silently thereafter.
  useEffect(() => {
    if (!reloadKey) return;
    load(true, true);
  }, [reloadKey, load]);

  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  // --- Lazy people -----------------------------------------------------------
  const loadPeople = useCallback((entryId: number) => {
    // Orphan bucket: its people are already in memory (positionsByRole), fed by the
    // effect below — never fetch (listPositions has no role_master_entry_id for them).
    if (entryId === ORPHAN_ENTRY_ID) return;
    setPeopleByEntry((prev) => {
      const existing = prev.get(entryId);
      // Skip if already loading or already loaded without error.
      if (existing && (existing.loading || !existing.error)) return prev;
      const next = new Map(prev);
      next.set(entryId, { loading: true, error: false, positions: existing?.positions ?? [] });
      return next;
    });

    // Page through the whole role — a single 500-row window would let the
    // client-side campus/dimension filters silently undercount on large roles
    // (e.g. Lecturer, ~1.4k seats), and would truncate the people list anyway.
    const PAGE = 500;
    const fetchAll = async (): Promise<Position[]> => {
      const acc: Position[] = [];
      for (let page = 0; page < 40; page++) {
        const rows = await organizationService.listPositions({
          role_master_entry_id: entryId,
          skip: page * PAGE,
          limit: PAGE,
        });
        acc.push(...rows);
        if (rows.length < PAGE) break;
      }
      return acc;
    };

    fetchAll()
      .then((all) => {
        // Exclude RETIRED seats so the people list matches the role's rollup
        // (the backend rollup counts non-RETIRED positions only).
        const positions = all.filter((p) => p.status !== 'RETIRED');
        setPeopleByEntry((prev) => {
          const next = new Map(prev);
          next.set(entryId, { loading: false, error: false, positions });
          return next;
        });
      })
      .catch((err) => {
        console.error('Failed to load positions for role', entryId, err);
        setPeopleByEntry((prev) => {
          const next = new Map(prev);
          next.set(entryId, { loading: false, error: true, positions: [] });
          return next;
        });
      });
  }, []);

  // --- Search index ----------------------------------------------------------
  const { searchIndex, pathById } = useMemo(() => {
    const searchIndex: IndexEntry[] = [];
    const pathById = new Map<string, string[]>();
    if (!data) return { searchIndex, pathById };

    const walk = (node: OrgStructureTreeNode, ancestors: string[], functionName: string) => {
      const path = [...ancestors, node.id];
      pathById.set(node.id, path);
      if (isRole(node)) {
        searchIndex.push({
          id: node.id, type: 'role', name: node.title, roleCode: node.role_code,
          entryId: node.entry_id, path, functionName,
        });
      } else {
        searchIndex.push({ id: node.id, type: node.type, name: node.name, path, functionName });
        node.children.forEach((c) => walk(c, path, functionName));
      }
    };
    data.functions.forEach((f) => walk(f, [], f.name));
    return { searchIndex, pathById };
  }, [data]);

  // --- Filter options --------------------------------------------------------
  const functionOptions = useMemo(() => (data ? data.functions.map((f) => f.name) : []), [data]);

  const gradeOptions = useMemo(() => {
    const s = new Set<string>();
    const walk = (n: OrgStructureTreeNode) => {
      if (isRole(n)) { if (n.grade_level) s.add(n.grade_level); }
      else n.children.forEach(walk);
    };
    data?.functions.forEach(walk);
    return Array.from(s).sort();
  }, [data]);

  // --- Pruned / scoped tree --------------------------------------------------
  const gradeKey = useMemo(() => Array.from(gradeFilter).sort().join(','), [gradeFilter]);

  const displayFunctions = useMemo(() => {
    if (!data) return [];
    const filters: Filters = { grades: gradeFilter, academicOnly, vacantOnly };
    const out: OrgStructureTreeContainer[] = [];
    for (const fn of data.functions) {
      if (functionFilter !== 'all' && fn.name !== functionFilter) continue;
      const pruned = pruneContainer(fn, filters);
      if (pruned) out.push(pruned);
    }
    return out;
    // gradeKey stands in for gradeFilter identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, functionFilter, gradeKey, academicOnly, vacantOnly]);

  const peopleFilterActive =
    campusFilter !== 'all' || dimCampusFilter !== 'all' || dimDeptFilter !== 'all' ||
    dimFacultyFilter !== 'all' || workerTypeFilter.size > 0 || positionTypeFilter.size > 0;

  const activeFilterCount =
    (functionFilter !== 'all' ? 1 : 0) + gradeFilter.size + (academicOnly ? 1 : 0) + (vacantOnly ? 1 : 0) +
    (campusFilter !== 'all' ? 1 : 0) + (dimCampusFilter !== 'all' ? 1 : 0) +
    (dimDeptFilter !== 'all' ? 1 : 0) + (dimFacultyFilter !== 'all' ? 1 : 0) +
    workerTypeFilter.size + positionTypeFilter.size;

  // Person-level predicate over a loaded Position (campus code + F&O financial
  // dimensions + worker type + position type — all multi-select, AND-composed).
  // Worker type reads the incumbent's employment_category (a vacant seat has none →
  // it can't match a worker-type filter); position type reads the seat's own
  // normalized F&O type (present incl. when vacant). Stable while the filters hold.
  const personFilter = useCallback(
    (p: Position): boolean => {
      if (campusFilter !== 'all' && p.campus_code !== campusFilter) return false;
      if (dimCampusFilter !== 'all' && p.dimension_campus !== dimCampusFilter) return false;
      if (dimDeptFilter !== 'all' && p.dimension_department !== dimDeptFilter) return false;
      if (dimFacultyFilter !== 'all' && p.dimension_faculty !== dimFacultyFilter) return false;
      if (workerTypeFilter.size > 0 && !workerTypeFilter.has(p.employment_category ?? '')) return false;
      if (positionTypeFilter.size > 0 && !positionTypeFilter.has(p.position_type_group ?? '')) return false;
      return true;
    },
    [campusFilter, dimCampusFilter, dimDeptFilter, dimFacultyFilter, workerTypeFilter, positionTypeFilter],
  );

  // Position->role resolver, taken VERBATIM from the server so the client buckets
  // positions IDENTICALLY (organization.py get_org_structure_tree `resolver`): it covers
  // ALL active roles, INCLUDING functionless ones that the server folds into `unassigned`
  // and never renders as tree nodes — deriving this from `data.functions` would miss them
  // and wrongly orphan their positions. Fallback (walk the rendered roles) only guards a
  // stale pre-`resolver` cached payload during rollout.
  const { activeEntryIds, foJobToEntry } = useMemo(() => {
    if (data?.resolver) {
      return {
        activeEntryIds: new Set<number>(data.resolver.active_entry_ids),
        foJobToEntry: new Map<string, number>(Object.entries(data.resolver.fo_job_to_entry)),
      };
    }
    const ids = new Set<number>();
    const fj = new Map<string, number>();
    const walk = (n: OrgStructureTreeNode) => {
      if (isRole(n)) {
        if (n.entry_id === ORPHAN_ENTRY_ID) return;
        ids.add(n.entry_id);
        if (n.fo_job_id && !fj.has(n.fo_job_id)) fj.set(n.fo_job_id, n.entry_id);
      } else {
        n.children.forEach(walk);
      }
    };
    (data?.functions ?? []).forEach(walk);
    return { activeEntryIds: ids, foJobToEntry: fj };
  }, [data]);

  // Non-RETIRED positions grouped by resolved role — the SINGLE source for BOTH the
  // person-level prune and the filtered rollup counts, so the two can never disagree.
  // Positions resolving to no active role land under ORPHAN_ENTRY_ID so they're
  // counted in the "Unassigned (no role)" branch instead of silently dropped.
  const positionsByRole = useMemo(() => {
    const m = new Map<number, Position[]>();
    for (const p of positions) {
      if (p.status === 'RETIRED') continue;
      let entry: number | undefined;
      const foJob = p.fo_job_id?.trim();                      // server keys fo_job_to_id on the STRIPPED value
      if (p.role_master_entry_id != null && activeEntryIds.has(p.role_master_entry_id)) {
        entry = p.role_master_entry_id;                       // tier 1: canonical FK to an active role
      } else if (foJob && foJobToEntry.has(foJob)) {
        entry = foJobToEntry.get(foJob);                      // tier 2: fo_job_id match (unreconciled)
      }
      const key = entry ?? ORPHAN_ENTRY_ID;                   // no active role → orphan bucket
      const arr = m.get(key);
      if (arr) arr.push(p);
      else m.set(key, [p]);
    }
    return m;
  }, [positions, activeEntryIds, foJobToEntry]);

  // Keep the orphan bucket's people row-set in sync (it isn't lazy-fetched — the
  // positions are already loaded). The people list still filters by personFilter at
  // render, so holding the full orphan set here is correct under any active filter.
  useEffect(() => {
    const orphans = positionsByRole.get(ORPHAN_ENTRY_ID) ?? [];
    setPeopleByEntry((prev) => {
      const next = new Map(prev);
      next.set(ORPHAN_ENTRY_ID, { loading: false, error: false, positions: orphans });
      return next;
    });
  }, [positionsByRole]);

  // --- Person-level (campus / dimension) PRUNE -------------------------------
  // A role SURVIVES iff at least one of its non-RETIRED positions passes the SAME
  // `personFilter` used for the people rows AND the filtered counts — a per-POSITION
  // conjunction, NOT independent per-facet coverage (which would wrongly keep a role
  // whose two active facets are satisfied by DIFFERENT seats, then render it with a
  // 0-seat count / "no people match" body). We walk the ALREADY grade/academic/
  // vacant/function-pruned `displayFunctions`, so the person-level filter truly
  // AND-composes with those at every level. `visibleIds` = surviving roles PLUS
  // every ancestor on their path. null when no person-level filter is active.
  const visibleIds = useMemo(() => {
    if (!peopleFilterActive) return null;
    const ids = new Set<string>();
    const roleMatches = (entryId: number): boolean =>
      (positionsByRole.get(entryId) ?? []).some(personFilter);
    const walk = (node: OrgStructureTreeNode) => {
      if (isRole(node)) {
        if (roleMatches(node.entry_id)) {
          const path = pathById.get(node.id);
          if (path) path.forEach((id) => ids.add(id));
        }
      } else {
        node.children.forEach(walk);
      }
    };
    displayFunctions.forEach(walk);
    return ids;
  }, [peopleFilterActive, displayFunctions, positionsByRole, personFilter, pathById]);

  // Filtered header rollups per node id. With the skeleton pruned, a Function must
  // NOT keep reading its full server total — so while the person-level filter is
  // active we recompute rollups over the surviving roles: a ROLE = its non-RETIRED
  // positions matching the filter (seats), incumbent!=null → filled, rest vacant;
  // a CONTAINER = the sum over its visible descendant roles (roles = their count).
  // We walk the grade/academic/vacant-pruned `displayFunctions` restricted to
  // `visibleIds`, so the numbers compose BOTH filter groups exactly as rendered.
  const filteredRollupById = useMemo(() => {
    if (!peopleFilterActive || !visibleIds) return null;
    const map = new Map<string, OrgStructureRollup>();
    const roleRollup = (entryId: number): OrgStructureRollup => {
      const ps = (positionsByRole.get(entryId) ?? []).filter(personFilter);
      let filled = 0;
      for (const p of ps) if (p.incumbent_user_id != null) filled += 1;
      // The orphan bucket is a pile of seats, not a real role → roles:0 (matches the
      // server's fn:__unassigned__ rollup so filtered + unfiltered role counts agree).
      const roles = entryId === ORPHAN_ENTRY_ID ? 0 : 1;
      return { roles, seats: ps.length, filled, vacant: ps.length - filled };
    };
    const walk = (node: OrgStructureTreeNode): OrgStructureRollup | null => {
      if (isRole(node)) {
        if (!visibleIds.has(node.id)) return null;
        const r = roleRollup(node.entry_id);
        map.set(node.id, r);
        return r;
      }
      let agg: OrgStructureRollup = { roles: 0, seats: 0, filled: 0, vacant: 0 };
      let any = false;
      for (const child of node.children) {
        const r = walk(child);
        if (r) {
          any = true;
          agg = { roles: agg.roles + r.roles, seats: agg.seats + r.seats, filled: agg.filled + r.filled, vacant: agg.vacant + r.vacant };
        }
      }
      if (!any) return null;
      map.set(node.id, agg);
      return agg;
    };
    displayFunctions.forEach(walk);
    return map;
  }, [peopleFilterActive, visibleIds, displayFunctions, positionsByRole, personFilter]);

  // Final render set: the grade/academic/vacant/function-pruned functions, further
  // narrowed to the person-level survivors when that filter is active.
  const renderFunctions = useMemo(() => {
    if (!peopleFilterActive || !visibleIds) return displayFunctions;
    return displayFunctions.filter((fn) => visibleIds.has(fn.id));
  }, [peopleFilterActive, visibleIds, displayFunctions]);

  // --- Search results --------------------------------------------------------
  const clientResults = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter((e) => e.name.toLowerCase().includes(q) || (e.roleCode?.toLowerCase().includes(q) ?? false))
      .sort((a, b) => {
        const as = a.name.toLowerCase().startsWith(q) ? 0 : 1;
        const bs = b.name.toLowerCase().startsWith(q) ? 0 : 1;
        return as - bs;
      })
      .slice(0, 15);
  }, [term, searchIndex]);

  // Debounced people search
  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setPeopleResults([]); setPeopleSearching(false); return; }
    setPeopleSearching(true);
    let stale = false;  // guards against out-of-order responses overwriting fresh ones
    const t = setTimeout(async () => {
      try {
        const res = await organizationService.listPositions({ query: q, limit: 20 });
        if (!stale) setPeopleResults(res);
      } catch {
        if (!stale) setPeopleResults([]);
      } finally {
        if (!stale) setPeopleSearching(false);
      }
    }, 300);
    return () => { stale = true; clearTimeout(t); };
  }, [term]);

  // --- Expand / collapse -----------------------------------------------------
  // While filtering, a non-role container toggles the filter-scoped
  // `collapsedInFilter` set (expanded-by-default); a ROLE always toggles the real
  // `expanded` set so its people stay lazy (you open a role to load its people).
  const toggle = useCallback((id: string, isRoleNode: boolean) => {
    const flip = (prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    };
    if (peopleFilterActive && !isRoleNode) setCollapsedInFilter(flip);
    else setExpanded(flip);
  }, [peopleFilterActive]);

  const collapseAll = useCallback(() => {
    // While filtering: collapse to the visible functions (mark them collapsed so
    // their subtrees hide) without touching the unfiltered `expanded` state.
    if (peopleFilterActive) setCollapsedInFilter(new Set(renderFunctions.map((f) => f.id)));
    else setExpanded(new Set());
    setFocusedPath(null);
  }, [peopleFilterActive, renderFunctions]);

  const expandFirstLevel = useCallback(() => {
    // While filtering: reveal everything (the filter default) by clearing the
    // user's collapses. Otherwise: expand only the top level.
    if (peopleFilterActive) { setCollapsedInFilter(new Set()); return; }
    if (!data) return;
    setExpanded(new Set(data.functions.map((f) => f.id)));
  }, [peopleFilterActive, data]);

  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  // --- Jump to a node --------------------------------------------------------
  const jumpTo = useCallback(
    (nodeId: string, opts?: { positionId?: number; entryId?: number }) => {
      const path = pathById.get(nodeId);
      if (!path) return;

      // Jumping to a search result takes priority over active filters — clear
      // every filter (role-level AND person-level) so the target is never pruned
      // or hidden (otherwise the breadcrumb points at a row that was filtered out).
      setFunctionFilter('all');
      setGradeFilter(new Set());
      setAcademicOnly(false);
      setVacantOnly(false);
      setCampusFilter('all');
      setDimCampusFilter('all');
      setDimDeptFilter('all');
      setDimFacultyFilter('all');
      setWorkerTypeFilter(new Set());
      setPositionTypeFilter(new Set());
      setCollapsedInFilter(new Set()); // fresh filter session next time

      setExpanded((prev) => {
        const next = new Set(prev);
        path.forEach((id) => next.add(id));
        return next;
      });
      setFocusedPath(path);
      setHighlightId(nodeId);
      setHighlightPositionId(opts?.positionId ?? null);
      if (opts?.entryId != null) loadPeople(opts.entryId);
      setShowResults(false);

      requestAnimationFrame(() => {
        setTimeout(() => {
          rowRefs.current.get(nodeId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
      });

      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => {
        setHighlightId(null);
        setHighlightPositionId(null);
      }, 2600);
    },
    [pathById, loadPeople],
  );

  const clearSearch = useCallback(() => {
    setTerm('');
    setShowResults(false);
    setPeopleResults([]);
  }, []);

  const toggleGrade = useCallback((g: string) => {
    setGradeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  }, []);

  const toggleWorkerType = useCallback((e: string) => {
    setWorkerTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);

  const togglePositionType = useCallback((e: string) => {
    setPositionTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);

  const clearFilters = useCallback(() => {
    setFunctionFilter('all');
    setGradeFilter(new Set());
    setAcademicOnly(false);
    setVacantOnly(false);
    setCampusFilter('all');
    setDimCampusFilter('all');
    setDimDeptFilter('all');
    setDimFacultyFilter('all');
    setWorkerTypeFilter(new Set());
    setPositionTypeFilter(new Set());
    setCollapsedInFilter(new Set()); // fresh filter session next time
  }, []);

  // --- Footer totals ---------------------------------------------------------
  const totals = useMemo(() => {
    if (!data) return { functions: 0, roles: 0, seats: 0 };
    let roles = 0;
    let seats = 0;
    data.functions.forEach((f) => { roles += f.rollup.roles; seats += f.rollup.seats; });
    roles += data.unassigned.roles;
    seats += data.unassigned.seats;
    // The synthetic "Unassigned (no role)" branch is a seat bucket, not a real
    // Function — count its seats (via the forEach above) but not as a function.
    const realFunctions = data.functions.filter((f) => f.id !== ORPHAN_FUNCTION_ID).length;
    return { functions: realFunctions, roles, seats };
  }, [data]);

  // Breadcrumb names for the focused node.
  const breadcrumb = useMemo(() => {
    if (!focusedPath) return [];
    return focusedPath.map((id) => {
      const e = searchIndex.find((x) => x.id === id);
      return e ? e.name : id;
    });
  }, [focusedPath, searchIndex]);

  // =============================================================================
  // RENDER
  // =============================================================================

  if (loading) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-border bg-card">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading organisation structure…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-border bg-card">
        <AlertCircle className="mb-3 h-8 w-8 text-destructive" />
        <p className="mb-3 text-sm text-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => load(true)}>
          <RefreshCw className="mr-2 h-4 w-4" /> Retry
        </Button>
      </div>
    );
  }

  const hasResults = clientResults.length > 0 || peopleResults.length > 0 || peopleSearching;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 p-3">
        {/* Search */}
        <div ref={searchBoxRef} className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => { setTerm(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search roles, teams or people…"
            className="h-9 pl-9 pr-8"
          />
          {term && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {showResults && term.trim() && (
            <>
              <div className="absolute left-0 top-full z-50 mt-1 max-h-[24rem] w-full min-w-[22rem] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
                {!hasResults && (
                  <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matches</div>
                )}

                {clientResults.length > 0 && (
                  <div>
                    <div className="sticky top-0 bg-popover px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Roles &amp; structure
                    </div>
                    {clientResults.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => jumpTo(r.id, r.type === 'role' ? { entryId: r.entryId } : undefined)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted"
                      >
                        {r.type === 'role' ? (
                          <Briefcase className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : r.type === 'function' ? (
                          <Building2 className="h-4 w-4 shrink-0 text-heritage-blue dark:text-heritage-blue-200" />
                        ) : (
                          <Folder className="h-4 w-4 shrink-0 text-future-blue-600 dark:text-future-blue-300" />
                        )}
                        {r.roleCode && (
                          <span className="shrink-0 font-mono text-xs text-heritage-blue dark:text-heritage-blue-100">{r.roleCode}</span>
                        )}
                        <span className="truncate text-foreground">{r.name}</span>
                        <span className="ml-auto shrink-0 text-[11px] capitalize text-muted-foreground">{r.type}</span>
                      </button>
                    ))}
                  </div>
                )}

                {(peopleResults.length > 0 || peopleSearching) && (
                  <div>
                    <div className="sticky top-0 bg-popover px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      People
                    </div>
                    {peopleSearching && peopleResults.length === 0 && (
                      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Searching people…
                      </div>
                    )}
                    {peopleResults.map((p) => (
                      <PeopleResultRow
                        key={p.id}
                        position={p}
                        onClick={() => {
                          // Resolve to a role node the SAME way positionsByRole does;
                          // a position with no active role jumps to the orphan bucket.
                          let entry: number | undefined;
                          const foJob = p.fo_job_id?.trim();
                          if (p.role_master_entry_id != null && activeEntryIds.has(p.role_master_entry_id)) {
                            entry = p.role_master_entry_id;
                          } else if (foJob && foJobToEntry.has(foJob)) {
                            entry = foJobToEntry.get(foJob);
                          }
                          const entryId = entry ?? ORPHAN_ENTRY_ID;
                          const nodeId = entryId === ORPHAN_ENTRY_ID
                            ? 'role:__unassigned__'
                            : (searchIndex.find((e) => e.type === 'role' && e.entryId === entryId)?.id ?? `role:${p.role_code}`);
                          jumpTo(nodeId, { entryId, positionId: p.id });
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Function scope */}
        <Select value={functionFilter} onValueChange={setFunctionFilter}>
          <SelectTrigger className="h-9 w-[13rem]">
            <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
            <SelectValue placeholder="All functions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All functions</SelectItem>
            {functionOptions.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Grade multiselect */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', gradeFilter.size > 0 && 'border-heritage-blue')}>
              <Filter className="mr-1.5 h-3.5 w-3.5" /> Grade
              {gradeFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{gradeFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by grade</DropdownMenuLabel>
            {gradeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No grades</div>
            )}
            {gradeOptions.map((g) => (
              <DropdownMenuCheckboxItem
                key={g}
                checked={gradeFilter.has(g)}
                onCheckedChange={() => toggleGrade(g)}
                onSelect={(e) => e.preventDefault()}
              >
                <span className="text-xs font-medium">{g}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {gradeFilter.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setGradeFilter(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear grades
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Academic toggle */}
        <Button
          variant={academicOnly ? 'default' : 'outline'}
          size="sm"
          className="h-9"
          onClick={() => setAcademicOnly((v) => !v)}
        >
          <GraduationCap className="mr-1.5 h-3.5 w-3.5" /> Academic
        </Button>

        {/* Vacant-only toggle */}
        <Button
          variant={vacantOnly ? 'default' : 'outline'}
          size="sm"
          className={cn('h-9', vacantOnly && 'bg-amber-500 hover:bg-amber-500/90')}
          onClick={() => setVacantOnly((v) => !v)}
        >
          Vacant only
        </Button>

        {/* Person-level facets: physical campus + F&O financial dimensions (Coins).
            These PRUNE the role skeleton to roles with >=1 position matching the
            filter AND narrow the people shown under each expanded role. */}
        <div className="flex items-center self-stretch">
          <span className="mx-1 h-6 w-px bg-border" aria-hidden />
        </div>
        <Select value={campusFilter} onValueChange={setCampusFilter}>
          <SelectTrigger className="h-9 w-[140px]"><MapPin className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {campusOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimCampusFilter} onValueChange={setDimCampusFilter}>
          <SelectTrigger className="h-9 w-[150px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. campuses</SelectItem>
            {dimCampusOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimDeptFilter} onValueChange={setDimDeptFilter}>
          <SelectTrigger className="h-9 w-[170px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. departments</SelectItem>
            {dimDeptOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimFacultyFilter} onValueChange={setDimFacultyFilter}>
          <SelectTrigger className="h-9 w-[160px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Faculty" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All faculties</SelectItem>
            {dimFacultyOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Worker type = incumbent's employment_category (filled seats only). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', workerTypeFilter.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Worker type
              {workerTypeFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{workerTypeFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by worker type (filled seats)</DropdownMenuLabel>
            {workerTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No worker types</div>
            )}
            {workerTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={workerTypeFilter.has(e)}
                onCheckedChange={() => toggleWorkerType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {workerTypeFilter.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setWorkerTypeFilter(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear worker type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Position type = seat's F&O type normalized (present incl. when vacant). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', positionTypeFilter.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Position type
              {positionTypeFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{positionTypeFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by position type (incl. vacant)</DropdownMenuLabel>
            {positionTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No position types</div>
            )}
            {positionTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={positionTypeFilter.has(e)}
                onCheckedChange={() => togglePositionType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {positionTypeFilter.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setPositionTypeFilter(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear position type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {peopleFilterActive && (
          <span className="text-[11px] italic text-muted-foreground">prunes the tree to matching roles</span>
        )}

        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={clearFilters}>
            <X className="mr-1 h-3.5 w-3.5" /> Clear ({activeFilterCount})
          </Button>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-9" onClick={expandFirstLevel}>
            <ChevronsUpDown className="mr-1 h-3.5 w-3.5" /> Expand functions
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={collapseAll}>
            <ChevronsDownUp className="mr-1 h-3.5 w-3.5" /> Collapse all
          </Button>
        </div>
      </div>

      {/* Breadcrumb for the focused node */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-1.5 border-b border-border bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">Focused:</span>
          {breadcrumb.map((name, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              <span className={cn('truncate', i === breadcrumb.length - 1 && 'font-medium text-foreground')}>{name}</span>
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => { setFocusedPath(null); setHighlightId(null); }}
            className="ml-2 rounded p-0.5 hover:bg-muted"
            aria-label="Clear focus"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Tree */}
      <div className="min-h-0 flex-1 overflow-auto">
        {renderFunctions.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-16 text-center">
            <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">
              {peopleFilterActive
                ? 'No roles match the current campus / dimension filters'
                : 'No roles match the current view'}
            </p>
            <p className="text-xs text-muted-foreground">Try clearing the filters or search.</p>
          </div>
        ) : (
          renderFunctions.map((fn) => (
            <TreeBranch
              key={fn.id}
              node={fn}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              registerRef={registerRef}
              highlightId={highlightId}
              peopleByEntry={peopleByEntry}
              onLoadPeople={loadPeople}
              highlightPositionId={highlightPositionId}
              onOpenRole={onOpenRole}
              onOpenPerson={onOpenPerson}
              personFilter={personFilter}
              visibleIds={visibleIds}
              filteredRollupById={filteredRollupById}
              forceExpand={peopleFilterActive}
              collapsedInFilter={collapsedInFilter}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
        <span>
          {totals.functions.toLocaleString()} functions · {totals.roles.toLocaleString()} roles ·{' '}
          {totals.seats.toLocaleString()} seats
          {data && data.unassigned.roles > 0 && (
            <span className="text-amber-600 dark:text-amber-400"> · {data.unassigned.roles.toLocaleString()} unassigned</span>
          )}
        </span>
        {data && <span>Generated {new Date(data.generated_at).toLocaleString()}</span>}
      </div>
    </div>
  );
};

// People search-result row (needs its own component for the photo hook).
const PeopleResultRow: React.FC<{ position: Position; onClick: () => void }> = ({ position, onClick }) => {
  // A vacant seat has no incumbent → don't render it as an "Unknown" person; show
  // it as the (vacant) SEAT, identified by its title, matching the tree's rows.
  const vacant = position.is_vacant || !position.incumbent_user_id;
  const photo = useUserPhoto(vacant ? null : position.incumbent_user_id);
  const campus = position.campus_name || position.campus_code || '';
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted"
    >
      {vacant ? (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-amber-400 text-[10px] font-medium text-amber-600 dark:text-amber-300">
          V
        </span>
      ) : (
        <Avatar className="h-6 w-6 shrink-0">
          {photo && <AvatarImage src={photo} alt={position.incumbent_name || ''} />}
          <AvatarFallback className="bg-heritage-blue/10 text-[10px] text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
            {getInitials(position.incumbent_name)}
          </AvatarFallback>
        </Avatar>
      )}
      {vacant ? (
        <span className="truncate font-medium">
          <span className="text-amber-600 dark:text-amber-400">Vacant</span>
          <span className="text-muted-foreground"> · {position.title || position.position_code}</span>
        </span>
      ) : (
        <span className="truncate font-medium text-foreground">{position.incumbent_name || 'Unknown'}</span>
      )}
      {position.role_code && (
        <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{position.role_code}</span>
      )}
      {campus && <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{campus}</span>}
    </button>
  );
};

export default OrgStructureTree;
